/**
 * Cross-runtime built-in browser contract (见 22).
 *
 * These values cross renderer / extension-host / main boundaries, so keep them
 * platform-free and structured-cloneable.
 */

export const BROWSER_EDITOR_VIEW_ID = 'browser.editor';
/**
 * Fallback editor slot id when a caller has not yet bound a dockview
 * `instanceKey`. New editor opens mint a distinct slot and pass it as the
 * view instance key (见 22).
 */
export const BROWSER_DEFAULT_EDITOR_SLOT_ID = 'default';
export const BROWSER_BACK_IPC = 'hibro:browser:back';
export const BROWSER_FORWARD_IPC = 'hibro:browser:forward';
export const BROWSER_RELOAD_IPC = 'hibro:browser:reload';
export const BROWSER_HARD_RELOAD_IPC = 'hibro:browser:hard-reload';
export const BROWSER_CAPTURE_SCREENSHOT_IPC =
  'hibro:browser:capture-screenshot';
export const BROWSER_SET_ZOOM_IPC = 'hibro:browser:set-zoom';
export const BROWSER_FIND_IN_PAGE_IPC = 'hibro:browser:find-in-page';
export const BROWSER_STOP_FIND_IN_PAGE_IPC = 'hibro:browser:stop-find-in-page';
export const BROWSER_GET_SETTINGS_IPC = 'hibro:browser:get-settings';
export const BROWSER_UPDATE_SETTINGS_IPC = 'hibro:browser:update-settings';
export const BROWSER_CHOOSE_DOWNLOAD_LOCATION_IPC =
  'hibro:browser:choose-download-location';
export const BROWSER_CLEAR_COOKIES_IPC = 'hibro:browser:clear-cookies';
export const BROWSER_SET_SITE_PERMISSION_IPC =
  'hibro:browser:set-site-permission';
export const BROWSER_PERMISSION_REQUESTED_IPC =
  'hibro:browser:permission-requested';
export const BROWSER_RESOLVE_PERMISSION_REQUEST_IPC =
  'hibro:browser:resolve-permission-request';
export const BROWSER_PROTOCOL_REQUESTED_IPC =
  'hibro:browser:protocol-requested';
export const BROWSER_RESOLVE_PROTOCOL_REQUEST_IPC =
  'hibro:browser:resolve-protocol-request';
export const BROWSER_NAVIGATE_IPC = 'hibro:browser:navigate';
export const BROWSER_OPEN_EXTERNAL_IPC = 'hibro:browser:open-external';
export const BROWSER_OPEN_IPC = 'hibro:browser:open';
export const BROWSER_OPEN_DEVTOOLS_IPC = 'hibro:browser:open-devtools';
export const BROWSER_STATE_IPC = 'hibro:browser:state';
export const BROWSER_GET_STATE_IPC = 'hibro:browser:get-state';
export const BROWSER_ATTACH_EDITOR_SLOT_IPC =
  'hibro:browser:attach-editor-slot';
export const BROWSER_CREATE_TAB_IPC = 'hibro:browser:create-tab';
export const BROWSER_SELECT_TAB_IPC = 'hibro:browser:select-tab';
export const BROWSER_CLOSE_TAB_IPC = 'hibro:browser:close-tab';
export const BROWSER_DISPOSE_EDITOR_SLOT_IPC =
  'hibro:browser:dispose-editor-slot';
export const BROWSER_SUSPEND_EDITOR_SLOT_IPC =
  'hibro:browser:suspend-editor-slot';
export type BrowserPartition = 'persistent' | 'ephemeral';
export type BrowserPresentation = 'window' | 'editor';

export interface BrowserOpenOptions {
  url: string;
  /** Where to present the page. Defaults to the current workbench editor. */
  presentation?: BrowserPresentation;
  /**
   * Reuse an existing page whose **current** URL is exactly the same (and whose
   * partition matches). Standalone windows default to `false` (always a new
   * window tab). Editor presentation defaults to `true` (open-or-focus the
   * dockview tab already showing that URL) so chat/unowned links do not stack
   * duplicates; pass `false` to force a new editor tab.
   */
  reuse?: boolean;
  /** Cookie/storage lifetime. Defaults to `persistent`. */
  partition?: BrowserPartition;
  /** Applied only when the shared browser host window is first created. */
  width?: number;
  /** Applied only when the shared browser host window is first created. */
  height?: number;
}

/**
 * Browser-window request made directly from a trusted renderer user gesture.
 * Keeping the same option shape as `capabilities.openBrowser` lets source UIs
 * stay runtime-neutral while Web pre-opens its shell synchronously.
 */
export type UserActivationBrowserOpenOptions = BrowserOpenOptions & {
  presentation: 'window';
};

export interface UserActivationApi {
  openBrowser(options: UserActivationBrowserOpenOptions): Promise<void>;
}

export interface BrowserHandle {
  /** Page id inside the selected built-in browser host. */
  id: string;
}

export interface BrowserRuntimeCapabilities {
  /** Capture and save the currently visible page viewport. */
  screenshot: boolean;
  /** Reload the current page while bypassing Chromium's HTTP cache. */
  hardReload: boolean;
  /** Apply Chromium page zoom rather than scaling the host chrome. */
  pageZoom: boolean;
  /** Search text inside the actual page contents. */
  findInPage: boolean;
  /** Manage the Electron-backed download policy. */
  downloads: boolean;
  /** Clear cookies owned by the dedicated browser partition. */
  clearCookies: boolean;
  /** Review and change per-origin permission decisions. */
  sitePermissions: boolean;
  /**
   * Whether navigation events include page-initiated and redirected URLs.
   * Web iframe hosts can only report URLs initiated by trusted chrome.
   */
  completeNavigationEvents: boolean;
}

export type BrowserSitePermissionKind =
  | 'camera'
  | 'microphone'
  | 'notifications'
  | 'geolocation'
  | 'clipboard-read';

export interface BrowserSitePermission {
  origin: string;
  permission: BrowserSitePermissionKind;
  allowed: boolean;
}

export interface BrowserDesktopSettings {
  downloadLocation: string;
  askWhereToSave: boolean;
  sitePermissions: BrowserSitePermission[];
}

export interface BrowserDesktopSettingsUpdate {
  downloadLocation?: string;
  askWhereToSave?: boolean;
}

export interface BrowserPermissionRequest {
  id: string;
  pageId: string;
  origin: string;
  permissions: BrowserSitePermissionKind[];
}

export interface BrowserPermissionResponse {
  requestId: string;
  allowed: boolean;
  remember: boolean;
}

/** Page-initiated external protocol; chrome never receives the target URL. */
export interface BrowserProtocolRequest {
  id: string;
  pageId: string;
  origin: string;
  scheme: string;
}

export interface BrowserProtocolResponse {
  requestId: string;
  allowed: boolean;
}

export interface BrowserFindResult {
  activeMatchOrdinal: number;
  matches: number;
  finalUpdate: boolean;
}

export interface BrowserFindOptions {
  forward?: boolean;
  findNext?: boolean;
  matchCase?: boolean;
}

/** One tab's projection for the browser chrome tab strip. */
export interface BrowserTabState {
  id: string;
  url: string;
  title: string;
  loading: boolean;
  /**
   * The top document has reached `dom-ready` for the current navigation.
   * Subresources can still keep `loading` true; chrome reveals the page on
   * this flag instead of waiting for every request to stop.
   */
  documentReady?: boolean;
  canGoBack: boolean;
  canGoForward: boolean;
  /** Latest page-declared favicon URL, when the runtime can observe one. */
  favicon?: string;
  /** Monotonic revision incremented for each committed main-frame navigation. */
  navigationRevision?: number;
  /** Current Chromium page zoom factor. */
  zoomFactor?: number;
  /** Latest result produced by an active find-in-page request. */
  findResult?: BrowserFindResult;
  /** Per-tab document revision used by iframe hosts to recreate one page. */
  documentVersion?: number;
  error?: string;
}

/**
 * Host state pushed to the browser chrome. Active-page fields are
 * duplicated at the top level so the address bar / navigation buttons can stay
 * simple while the tab strip reads `tabs` + `activeTabId`.
 */
export interface BrowserState {
  /** Shared host window id. */
  id: string;
  presentation: BrowserPresentation;
  /**
   * Editor-hosted slot identity. Standalone windows omit this; editor chrome
   * uses the dockview `instanceKey` so each workbench tab has its own page.
   */
  slotId?: string;
  activeTabId: string;
  tabs: BrowserTabState[];
  url: string;
  title: string;
  loading: boolean;
  canGoBack: boolean;
  canGoForward: boolean;
  capabilities: BrowserRuntimeCapabilities;
  favicon?: string;
  navigationRevision?: number;
  zoomFactor?: number;
  findResult?: BrowserFindResult;
  /**
   * Monotonic document revision for hosts that must recreate their content
   * element to navigate or reload. Desktop WebContentsView hosts may omit it.
   */
  documentVersion?: number;
  error?: string;
}

/** Optional payload for chrome "new tab" (blank until the user navigates). */
export interface BrowserCreateTabOptions {
  url?: string;
  partition?: BrowserPartition;
}

/** Sender-bound preload control surface for trusted browser chrome. */
export interface BrowserChromeApi {
  /**
   * Bind a new or restored workbench editor instance to the runtime browser
   * host before any slot-bound control request.
   */
  attachEditorSlot?(slotId: string): Promise<BrowserState>;
  /** `slotId` selects one editor tab; omitted = standalone / last editor slot. */
  getState(slotId?: string): Promise<BrowserState | undefined>;
  navigate(url: string, slotId?: string): Promise<void>;
  goBack(slotId?: string): Promise<void>;
  goForward(slotId?: string): Promise<void>;
  reload(slotId?: string): Promise<void>;
  hardReload?(slotId?: string): Promise<void>;
  captureScreenshot?(slotId?: string): Promise<{ saved: boolean }>;
  setZoomFactor?(factor: number, slotId?: string): Promise<void>;
  findInPage?(
    text: string,
    options?: BrowserFindOptions,
    slotId?: string,
  ): Promise<void>;
  stopFindInPage?(keepSelection?: boolean, slotId?: string): Promise<void>;
  getDesktopSettings?(): Promise<BrowserDesktopSettings>;
  updateDesktopSettings?(
    update: BrowserDesktopSettingsUpdate,
  ): Promise<BrowserDesktopSettings>;
  chooseDownloadLocation?(): Promise<string | undefined>;
  clearCookies?(): Promise<void>;
  setSitePermission?(
    permission: BrowserSitePermission,
  ): Promise<BrowserDesktopSettings>;
  resolvePermissionRequest?(response: BrowserPermissionResponse): Promise<void>;
  onPermissionRequest?(
    listener: (request: BrowserPermissionRequest) => void,
  ): () => void;
  resolveProtocolRequest?(response: BrowserProtocolResponse): Promise<void>;
  onProtocolRequest?(
    listener: (request: BrowserProtocolRequest) => void,
  ): () => void;
  openExternal(slotId?: string): Promise<void>;
  openDevTools(slotId?: string): Promise<void>;
  createTab(options?: BrowserCreateTabOptions): Promise<BrowserHandle>;
  selectTab(tabId: string): Promise<void>;
  closeTab(tabId: string): Promise<void>;
  /**
   * Drop an editor slot (and its native page) when the workbench tab closes.
   * Also drops the saved URL. Standalone window chrome omits this.
   */
  disposeEditorSlot?(slotId: string): Promise<void>;
  /**
   * Drop the live editor page when its tab is parked for another partition,
   * but keep the saved URL so the same slot can reopen it (见 22).
   */
  suspendEditorSlot?(slotId: string): Promise<void>;
  onState(listener: (state: BrowserState) => void): () => void;
}
